import { useEffect, useMemo, useRef, useState } from "react";
import { ClientCombobox } from "@/components/ClientCombobox";
import { api, API_BASE } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, ClockCounterClockwise, UploadSimple, PaperPlaneTilt, Trash, FileText, Prohibit } from "@phosphor-icons/react";
import VoidInvoiceModal from "@/components/VoidInvoiceModal";
import { QuickView, QVRow, qvRowClick } from "@/components/QuickView";
import StatementModal from "@/components/StatementModal";
import { useColumnPrefs } from "@/lib/useColumnPrefs";
import { ColumnChooser } from "@/components/ColumnChooser";
import { money } from "@/lib/money";

const FILTERS = [
  { key: "outstanding", label: "Outstanding" },
  { key: "draft", label: "Draft" },
  { key: "paid", label: "Paid" },
  { key: "voided", label: "Voided" },
  { key: "all", label: "All" },
];

const INV_COLUMNS = [
  { key: "number", label: "Number", locked: true },
  { key: "client", label: "Client" },
  { key: "workers", label: "Workers" },
  { key: "week_ending", label: "Week Ending" },
  { key: "po_number", label: "PO #" },
  { key: "due_date", label: "Due" },
  { key: "sent_at", label: "Date Sent" },
  { key: "paid_date", label: "Date Paid" },
  { key: "total", label: "Total" },
  { key: "profit", label: "Profit" },
  { key: "status", label: "Status" },
  { key: "last_reminder", label: "Last Reminder" },
];

// Latest recorded payment date — the "date paid" for paid invoices; for partial
// payments it's the most recent installment.
const lastReminder = (i) => { const r = i.reminders || []; return r.length ? String(r[r.length - 1].sent_at || "") : ""; };
const paidDateOf = (i) => {
  const dates = (i.payments || []).map((p) => String(p.date || "").slice(0, 10)).filter(Boolean).sort();
  return dates.length ? dates[dates.length - 1] : "";
};

export default function Invoices() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [remindingId, setRemindingId] = useState(null);
  const [markPaidFor, setMarkPaidFor] = useState(null);
  const [payDate, setPayDate] = useState("");
  const [payMethod, setPayMethod] = useState("check");
  const [payBusy, setPayBusy] = useState(false);
  const [sortBy, setSortBy] = useState({ col: "week_ending", dir: "desc" });
  // Secondary sort ("Then by …") — user picks a second key from a dropdown so
  // ties on the primary column are broken deterministically. Persisted via
  // localStorage so their preference survives reloads.
  const SORT_COLS = [
    { key: "none", label: "— none —" },
    { key: "number", label: "Invoice #" },
    { key: "client", label: "Client" },
    { key: "week_ending", label: "Week ending" },
    { key: "po_number", label: "PO #" },
    { key: "due_date", label: "Due date" },
    { key: "sent_at", label: "Date sent" },
    { key: "paid_date", label: "Date paid" },
    { key: "total", label: "Total" },
    { key: "profit", label: "Profit" },
    { key: "status", label: "Status" },
  ];
  const [sortBy2, setSortBy2] = useState(() => {
    try {
      const raw = localStorage.getItem("sos-invoices-sort2");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore corrupt json */ }
    return { col: "week_ending", dir: "desc" };
  });
  useEffect(() => {
    try { localStorage.setItem("sos-invoices-sort2", JSON.stringify(sortBy2)); }
    catch { /* localStorage quota — ignore */ }
  }, [sortBy2]);
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { total, done, ok, failed }

  const toggleSort = (col) => {
    setSortBy((prev) => prev.col === col
      ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { col, dir: col === "week_ending" || col === "due_date" || col === "total" || col === "balance_due" ? "desc" : "asc" });
  };
  const sortIcon = (col) => sortBy.col === col ? (sortBy.dir === "asc" ? " ↑" : " ↓") : "";
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState(() => {
    if (typeof window === "undefined") return "outstanding";
    const q = new URLSearchParams(window.location.search).get("filter");
    return ["outstanding", "draft", "paid", "voided", "all"].includes(q) ? q : "outstanding";
  });
  const [voidFor, setVoidFor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [historic, setHistoric] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [viewI, setViewI] = useState(null);
  const { vis, hiddenSet, toggleColumn, resetColumns } = useColumnPrefs("invoices", INV_COLUMNS);
  const visColCount = INV_COLUMNS.length - hiddenSet.size + 1 + (isAdmin ? 1 : 0);

  const [blankForm, setBlankForm] = useState({ client_id: "", project_id: "", payment_terms: "Net 30", due_date: "" });
  const [histForm, setHistForm] = useState({
    client_id: "", amount: "", invoice_date: "", week_ending: "", due_date: "",
    payment_terms: "Net 30", number: "", po_number: "", subcontractor_number: "",
    project_id: "", project_name: "", project_location: "", description: "",
    is_paid: false, paid_date: "",
  });

  const load = () => api.get("/invoices").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : []));
    api.get("/projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : []));
  }, []);

  const [clientFilter, setClientFilter] = useState("");
  const [numQuery, setNumQuery] = useState("");

  const clientRows = useMemo(() => {
    let base = clientFilter ? rows.filter((i) => i.client_id === clientFilter) : rows;
    const q = numQuery.trim().toLowerCase();
    if (q) base = base.filter((i) => (i.number || "").toLowerCase().includes(q));
    return base;
  }, [rows, clientFilter, numQuery]);

  const filtered = useMemo(() => {
    let base = clientRows;
    const searching = numQuery.trim().length > 0;
    if (!searching) {
      if (filter === "outstanding") base = clientRows.filter((i) => ["sent", "partially_paid", "overdue"].includes(i.status) && (i.balance_due || 0) > 0);
      else if (filter === "draft") base = clientRows.filter((i) => i.status === "draft");
      else if (filter === "paid") base = clientRows.filter((i) => i.status === "paid");
      else if (filter === "voided") base = clientRows.filter((i) => i.status === "voided");
    }
    const cnames = new Map(clients.map((c) => [c.id, c.name]));
    const getVal = (i, col) => {
      switch (col) {
        case "number": return i.number || "";
        case "client": return cnames.get(i.client_id) || "";
        case "week_ending": return i.week_ending || "";
        case "po_number": return i.po_number || "";
        case "due_date": return i.due_date || "";
        case "sent_at": return String(i.sent_at || "");
        case "paid_date": return paidDateOf(i);
        case "total": return Number(i.total || 0);
        case "profit": return Number(i.total || 0) - Number(i.pay_total ?? i.total ?? 0);
        case "status": return i.status || "";
        case "last_reminder": return lastReminder(i) || "";
        default: return "";
      }
    };
    const dir = sortBy.dir === "asc" ? 1 : -1;
    const cmp = (a, b, col, mul) => {
      const va = getVal(a, col);
      const vb = getVal(b, col);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
      return String(va).localeCompare(String(vb)) * mul;
    };
    return [...base].sort((a, b) => {
      const primary = cmp(a, b, sortBy.col, dir);
      if (primary !== 0) return primary;
      // Secondary tiebreaker — user-selectable via the "Then by" dropdown.
      if (sortBy2.col && sortBy2.col !== "none" && sortBy2.col !== sortBy.col) {
        const sDir = sortBy2.dir === "asc" ? 1 : -1;
        const secondary = cmp(a, b, sortBy2.col, sDir);
        if (secondary !== 0) return secondary;
      }
      // Tertiary: invoice number asc for stable ordering
      return String(a.number || "").localeCompare(String(b.number || ""));
    });
  }, [clientRows, filter, sortBy, sortBy2, clients, numQuery]);

  const counts = useMemo(() => ({
    outstanding: clientRows.filter((i) => ["sent", "partially_paid", "overdue"].includes(i.status) && (i.balance_due || 0) > 0).length,
    draft: clientRows.filter((i) => i.status === "draft").length,
    paid: clientRows.filter((i) => i.status === "paid").length,
    voided: clientRows.filter((i) => i.status === "voided").length,
    all: clientRows.length,
  }), [clientRows]);

  // Same rule as the Dashboard / aging / forecast: unsent drafts are not outstanding.
  const totalOutstanding = useMemo(() =>
    rows.filter((i) => ["sent", "partially_paid", "overdue"].includes(i.status))
        .reduce((s, i) => s + (i.balance_due || 0), 0),
  [rows]);
  const draftsUnsent = useMemo(() => {
    const d = rows.filter((i) => i.status === "draft");
    return { count: d.length, total: d.reduce((s, i) => s + (i.balance_due || 0), 0) };
  }, [rows]);

  // ---- Bulk send drafts ----
  const draftIdsInView = useMemo(
    () => filtered.filter((i) => i.status === "draft").map((i) => i.id),
    [filtered]
  );
  const selectedCount = selected.size;
  const allDraftsInViewSelected = draftIdsInView.length > 0 && draftIdsInView.every((id) => selected.has(id));

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllInView = () => {
    setSelected((prev) => {
      if (allDraftsInViewSelected) {
        const next = new Set(prev);
        draftIdsInView.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      draftIdsInView.forEach((id) => next.add(id));
      return next;
    });
  };

  const bulkSend = async () => {
    const ids = filtered.filter((i) => i.status === "draft" && selected.has(i.id)).map((i) => i.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Send ${ids.length} draft invoice${ids.length === 1 ? "" : "s"} to their clients now?\n\nEach client will receive the invoice PDF + timesheet detail PDF by email, BCC to billing.`)) return;
    setBulkSending(true);
    setBulkProgress({ total: ids.length, done: 0, ok: 0, failed: 0, errors: [] });
    let ok = 0, failed = 0, done = 0;
    const errors = [];
    for (const iid of ids) {
      try {
        const { data } = await api.post(`/invoices/${iid}/send`);
        if (data.email_sent === false) {
          failed += 1;
          const inv = rows.find((r) => r.id === iid);
          errors.push({ number: inv?.number || iid.slice(0, 8), msg: data.email_error || "email failed" });
        } else {
          ok += 1;
        }
      } catch (e) {
        failed += 1;
        const inv = rows.find((r) => r.id === iid);
        errors.push({ number: inv?.number || iid.slice(0, 8), msg: e.response?.data?.detail?.toString() || e.message });
      }
      done += 1;
      setBulkProgress({ total: ids.length, done, ok, failed, errors });
    }
    setBulkSending(false);
    setSelected(new Set());
    if (failed === 0) toast.success(`Sent ${ok} invoice${ok === 1 ? "" : "s"}.`);
    else toast.error(`Sent ${ok} / ${ok + failed} — ${failed} failed. See details.`);
    load();
  };

  const remind = async (iid) => {
    setRemindingId(iid);
    try {
      const { data } = await api.post(`/invoices/${iid}/remind`);
      toast.success(`Reminder sent to ${data.to}${data.reminders_count > 1 ? ` (${data.reminders_count} total)` : ""}.`);
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setRemindingId(null); }
  };

  const [resendingId, setResendingId] = useState(null);
  const resendInvoice = async (inv) => {
    if (!window.confirm(`Re-send invoice ${inv.number} to the client's billing email now?\n\nThe client will receive the invoice PDF + timesheet detail again.`)) return;
    setResendingId(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/send`);
      if (data.email_sent) toast.success(`Invoice ${inv.number} re-sent to the client.`);
      else toast.error(`Send failed again: ${data.email_error || "unknown error"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setResendingId(null); }
  };

  const deleteInvoice = async (inv) => {
    const paid = Number(inv.total || 0) - Number(inv.balance_due || 0);
    const warning = inv.status === "paid"
      ? `\n\n⚠  This invoice is FULLY PAID (${money(paid)} recorded). Deleting will also remove that payment record. Consider recording a refund instead.`
      : (inv.status === "partially_paid" || paid > 0)
        ? `\n\n⚠  ${money(paid)} in payments have been recorded against this invoice. Deleting removes those payment records too.`
        : inv.status === "sent" || inv.status === "overdue"
          ? "\n\nThis invoice has already been sent to the client. Deleting it does NOT retract the email — you may want to notify them separately."
          : "";
    if (!window.confirm(
      `Permanently delete invoice ${inv.number}?\n\nStatus: ${inv.status}  ·  Total: ${money(inv.total)}${warning}\n\nAny linked timesheets will be released back to "approved" so they can be re-invoiced. This action is audit-logged but cannot be undone.${inv.status !== "voided" ? "\n\nTip: use Void instead if you want to keep this invoice in your records." : ""}`
    )) return;
    try {
      const { data } = await api.delete(`/invoices/${inv.id}`);
      toast.success(`Invoice ${inv.number} deleted${data.released_timesheets ? ` · ${data.released_timesheets} timesheet(s) released` : ""}.`);
      setSelected((prev) => { const n = new Set(prev); n.delete(inv.id); return n; });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    }
  };

  const openMarkPaid = (inv) => {
    setMarkPaidFor(inv);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayMethod("check");
  };

  const doMarkPaid = async (e) => {
    e.preventDefault();
    if (!markPaidFor) return;
    if (!payDate) { toast.error("Payment date is required"); return; }
    setPayBusy(true);
    try {
      const { data } = await api.post(`/invoices/${markPaidFor.id}/payments`, {
        amount: markPaidFor.balance_due,
        method: payMethod,
        date: payDate,
        note: "Marked paid from invoice list",
      });
      if (data.status === "paid") {
        toast.success(`Invoice ${markPaidFor.number} marked PAID on ${payDate}.`);
      } else {
        toast.success(`Payment recorded. Remaining balance: $${data.balance_due.toFixed(2)}`);
      }
      setMarkPaidFor(null); load();
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
    finally { setPayBusy(false); }
  };

  const createBlank = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/invoices", blankForm);
      toast.success(`Draft invoice ${data.number} created.`);
      setCreating(false);
      setBlankForm({ client_id: "", project_id: "", payment_terms: "Net 30", due_date: "" });
      nav(`/invoices/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  const createHistoric = async (e) => {
    e.preventDefault();
    if (!histForm.client_id) { toast.error("Please select a client"); return; }
    const amt = parseFloat(histForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    try {
      const payload = { ...histForm, amount: amt };
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || payload[k] === null) delete payload[k]; });
      const { data } = await api.post("/invoices/historic", payload);
      const paidWord = histForm.is_paid ? "paid" : `${money(data.total)} outstanding`;
      toast.success(`Historic invoice ${data.number} recorded (${paidWord}).`);
      setHistoric(false);
      setHistForm({ client_id: "", amount: "", invoice_date: "", week_ending: "", due_date: "",
                    payment_terms: "Net 30", number: "", po_number: "", subcontractor_number: "",
                    project_id: "", project_name: "", project_location: "", description: "",
                    is_paid: false, paid_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  const blankProjects = blankForm.client_id ? projects.filter((p) => p.client_id === blankForm.client_id) : [];
  const histProjects = histForm.client_id ? projects.filter((p) => p.client_id === histForm.client_id) : [];
  const csvInputRef = useRef(null);
  const [importResult, setImportResult] = useState(null);
  const doCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/historic/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} of ${data.total_rows} historic invoices${data.errors?.length ? ` — ${data.errors.length} skipped` : ""}.`);
      else toast.error(`No rows imported — ${data.errors?.length || 0} error(s). See details below.`);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
    finally { if (csvInputRef.current) csvInputRef.current.value = ""; }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="tinylabel">Receivables</div>
          <h1 className="text-3xl font-black">Invoices</h1>
          <div className="text-slate-500 text-sm mt-1" data-testid="invoices-total-outstanding">Total outstanding balance: <span className="font-mono font-bold text-slate-900">{money(totalOutstanding)}</span>
            {draftsUnsent.count > 0 && <span className="ml-2 text-xs text-amber-700" data-testid="invoices-drafts-unsent" title="Drafts are not outstanding until they are sent">· plus {money(draftsUnsent.total)} in {draftsUnsent.count} unsent draft{draftsUnsent.count === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a data-testid="export-invoices-csv" href={`${API_BASE}/exports/invoices.csv`}
             className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50 flex items-center">Export CSV</a>
          <button data-testid="statement-btn" onClick={() => setShowStatement(true)}
                  className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50 flex items-center gap-2">
            <FileText size={18} weight="bold" /> Statement
          </button>
          {isAdmin && (
            <>
              <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden"
                data-testid="csv-import-input" onChange={doCsvImport} />
              <a data-testid="csv-template-btn" href={`${API_BASE}/invoices/historic/csv-template`}
                className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50 flex items-center gap-2 text-slate-700">
                Download template
              </a>
              <button data-testid="csv-import-btn" onClick={() => csvInputRef.current?.click()}
                title="CSV columns: client, amount, week_ending (YYYY-MM-DD), date_paid (blank = still outstanding). Download template for a working example."
                className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50 flex items-center gap-2">
                <UploadSimple size={18} weight="bold" /> Import CSV
              </button>
            </>
          )}
          {isAdmin && (
            <button data-testid="add-historic-invoice-btn" onClick={() => setHistoric(true)}
                    className="h-11 px-4 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-md flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="bold" /> Add historic invoice
            </button>
          )}
          {isAdmin && (
            <button data-testid="new-invoice-btn" onClick={() => setCreating(true)}
                    className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2">
              <Plus size={18} weight="bold" /> New invoice
            </button>
          )}
        </div>
      </div>

      {isAdmin && selectedCount > 0 && (
        <div data-testid="bulk-send-bar" className="mb-4 border-2 border-green-600 bg-green-50 rounded-md p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <b>{selectedCount}</b> draft invoice{selectedCount === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm bg-white hover:bg-slate-50"
            >Clear</button>
            <button
              type="button"
              onClick={bulkSend}
              disabled={bulkSending}
              data-testid="bulk-send-btn"
              className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md text-sm flex items-center gap-2 disabled:opacity-60"
            >
              <PaperPlaneTilt size={16} weight="bold" />
              {bulkSending ? `Sending ${bulkProgress?.done || 0}/${bulkProgress?.total || 0}…` : `Send ${selectedCount} draft${selectedCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}

      {bulkProgress && !bulkSending && bulkProgress.errors?.length > 0 && (
        <div data-testid="bulk-send-errors" className="mb-4 border rounded-md p-3 bg-white border-rose-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-rose-700">
              <b>Bulk send:</b> {bulkProgress.ok} sent · {bulkProgress.failed} failed
            </div>
            <button onClick={() => setBulkProgress(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          <ul className="mt-2 text-xs text-slate-700 space-y-1 max-h-40 overflow-y-auto">
            {bulkProgress.errors.map((er, i) => (
              <li key={i}><span className="font-mono text-slate-400">{er.number}:</span> {er.msg}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex border border-slate-300 rounded-md overflow-hidden bg-white" role="tablist">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`invoice-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 h-10 text-sm font-semibold border-r border-slate-200 last:border-r-0 transition-colors ${filter === f.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {f.label} <span className={`ml-1 text-xs ${filter === f.key ? "text-slate-300" : "text-slate-400"}`}>({counts[f.key]})</span>
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 relative">
          <input
            value={numQuery}
            onChange={(e) => setNumQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setNumQuery(""); }}
            data-testid="invoice-number-search"
            placeholder="Invoice # …"
            className="h-10 w-44 pl-3 pr-8 border border-slate-300 rounded-md bg-white text-sm font-mono"
            title="Type any part of an invoice number — searches all statuses"
          />
          {numQuery && (
            <button type="button" data-testid="invoice-number-search-clear"
              onClick={() => setNumQuery("")}
              className="absolute right-2 text-slate-400 hover:text-slate-700 text-sm font-bold">
              ✕
            </button>
          )}
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="tinylabel">Client:</span>
          <div className="w-64" data-testid="invoice-client-filter">
            <ClientCombobox
              value={clientFilter}
              onChange={(v) => setClientFilter(v)}
              clients={clients}
              placeholder="All clients"
              testId="invoice-client-filter-combobox"
            />
          </div>
          {clientFilter && (
            <button type="button" data-testid="invoice-client-filter-clear"
              onClick={() => setClientFilter("")}
              title="Show all clients"
              className="h-9 px-2 border border-slate-300 rounded-md bg-white hover:bg-slate-50 text-slate-500 text-sm font-semibold">
              ✕
            </button>
          )}
          {clientFilter && (() => {
            const open = rows.filter((i) => i.client_id === clientFilter
              && ["draft", "sent", "partially_paid", "overdue"].includes(i.status)
              && (i.balance_due || 0) > 0);
            const bal = open.reduce((s, i) => s + (i.balance_due || 0), 0);
            return (
              <span data-testid="client-balance-chip"
                title={`${open.length} open invoice${open.length === 1 ? "" : "s"} for this client`}
                className={`h-9 inline-flex items-center gap-1 px-3 rounded-full text-sm font-bold border ${bal > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}>
                {bal > 0 ? <>Owes {money(bal)} <span className="font-semibold text-amber-600">({open.length} open)</span></> : "Paid up — $0.00 outstanding"}
              </span>
            );
          })()}
        </div>
        <div className="inline-flex items-center gap-2 text-sm text-slate-600">
          <span className="tinylabel">Sort:</span>
          <span className="font-mono text-xs text-slate-500">
            {SORT_COLS.find((c) => c.key === sortBy.col)?.label || sortBy.col} {sortBy.dir === "asc" ? "↑" : "↓"}
          </span>
          <span className="text-slate-400">then by</span>
          <select
            data-testid="invoice-sort2-col"
            value={sortBy2.col}
            onChange={(e) => setSortBy2((s) => ({ ...s, col: e.target.value }))}
            className="h-9 px-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            {SORT_COLS.filter((c) => c.key !== sortBy.col).map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          {sortBy2.col !== "none" && (
            <button
              type="button"
              data-testid="invoice-sort2-dir"
              onClick={() => setSortBy2((s) => ({ ...s, dir: s.dir === "asc" ? "desc" : "asc" }))}
              className="h-9 w-9 border border-slate-300 rounded-md bg-white hover:bg-slate-50 font-mono text-sm"
              title={`Toggle secondary sort direction (currently ${sortBy2.dir === "asc" ? "ascending" : "descending"})`}
            >
              {sortBy2.dir === "asc" ? "↑" : "↓"}
            </button>
          )}
        </div>
        <div className="ml-auto">
          <ColumnChooser columns={INV_COLUMNS} hiddenSet={hiddenSet} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>

      {importResult && (
        <div data-testid="csv-import-result" className="mb-4 border rounded-md p-4 bg-white border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <b>CSV import:</b> {importResult.imported} imported / {importResult.total_rows} rows
              {importResult.errors?.length ? <span className="text-rose-600 ml-2">({importResult.errors.length} skipped)</span> : null}
            </div>
            <button onClick={() => setImportResult(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          {importResult.errors?.length > 0 && (
            <ul className="mt-2 text-xs text-slate-600 space-y-1 max-h-40 overflow-y-auto">
              {importResult.errors.slice(0, 30).map((er, i) => (
                <li key={i}><span className="font-mono text-slate-400">row {er.row}:</span> {er.error}</li>
              ))}
              {importResult.errors.length > 30 && <li className="text-slate-400">… and {importResult.errors.length - 30} more</li>}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              {isAdmin && (
                <th className="px-3 py-3 text-left w-8">
                  {draftIdsInView.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allDraftsInViewSelected}
                      onChange={toggleSelectAllInView}
                      aria-label="Select all drafts"
                      data-testid="select-all-drafts"
                      className="h-4 w-4 accent-green-600 cursor-pointer"
                    />
                  )}
                </th>
              )}
              {[
                { key: "number", label: "Number" },
                { key: "client", label: "Client" },
                { key: "workers", label: "Workers", plain: true },
                { key: "week_ending", label: "Week Ending" },
                { key: "po_number", label: "PO #" },
                { key: "due_date", label: "Due" },
                { key: "sent_at", label: "Date Sent" },
                { key: "paid_date", label: "Date Paid" },
              ].filter((h) => vis(h.key)).map((h) => h.plain ? (
                <th key={h.key} className="text-left px-4 py-3">{h.label}</th>
              ) : (
                <th key={h.key} className="text-left px-4 py-3 cursor-pointer select-none hover:text-slate-800"
                    data-testid={`invoice-sort-${h.key}`} onClick={() => toggleSort(h.key)}>
                  {h.label}<span className="font-mono">{sortIcon(h.key)}</span>
                </th>
              ))}
              {vis("total") && <th className="text-right px-4 py-3 cursor-pointer select-none hover:text-slate-800"
                  data-testid="invoice-sort-total" onClick={() => toggleSort("total")}>Total<span className="font-mono">{sortIcon("total")}</span></th>}
              {vis("profit") && <th className="text-right px-4 py-3 cursor-pointer select-none hover:text-slate-800"
                  data-testid="invoice-sort-profit" onClick={() => toggleSort("profit")}>Profit<span className="font-mono">{sortIcon("profit")}</span></th>}
              {vis("status") && <th className="text-left px-4 py-3 cursor-pointer select-none hover:text-slate-800"
                  data-testid="invoice-sort-status" onClick={() => toggleSort("status")}>Status<span className="font-mono">{sortIcon("status")}</span></th>}
              {vis("last_reminder") && <th className="text-left px-4 py-3 cursor-pointer select-none hover:text-slate-800" title="Most recent payment reminder emailed to the client (manual only — nothing is sent automatically)"
                  data-testid="invoice-sort-last_reminder" onClick={() => toggleSort("last_reminder")}>Last Reminder<span className="font-mono">{sortIcon("last_reminder")}</span></th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={visColCount} className="px-4 py-6 text-slate-400">
                {rows.length === 0
                  ? "No invoices yet. Use New invoice for a fresh draft, or Add historic invoice for pre-SOS outstanding balances."
                  : `No invoices match the "${FILTERS.find((f) => f.key === filter)?.label}" filter.`}
              </td></tr>
            )}
            {filtered.map((i) => {
              const canRemind = isAdmin && ["sent", "partially_paid", "overdue"].includes(i.status);
              const clientName = clients.find((c) => c.id === i.client_id)?.name || "—";
              const isDraft = i.status === "draft";
              const isVoided = i.status === "voided";
              const voidTitle = isVoided ? `VOIDED${i.voided_at ? ` ${String(i.voided_at).slice(0, 10)}` : ""}${i.void_reason ? ` — ${i.void_reason}` : ""}${i.void_released_timesheets === false && i.void_retained_timesheet_count ? ` · ${i.void_retained_timesheet_count} timesheet(s) kept, not re-billed` : ""}` : undefined;
              return (
                <tr key={i.id} onClick={(e) => isAdmin && qvRowClick(e, () => setViewI(i))} title={voidTitle}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${selected.has(i.id) ? "bg-green-50/70" : ""} ${isAdmin ? "cursor-pointer" : ""} ${isVoided ? "text-slate-400 bg-slate-50/60" : ""}`} data-testid={`invoice-row-${i.id}`}>
                  {isAdmin && (
                    <td className="px-3 py-3">
                      {isDraft && (
                        <input
                          type="checkbox"
                          checked={selected.has(i.id)}
                          onChange={() => toggleSelected(i.id)}
                          aria-label={`Select ${i.number}`}
                          data-testid={`select-invoice-${i.id}`}
                          className="h-4 w-4 accent-green-600 cursor-pointer"
                        />
                      )}
                    </td>
                  )}
                  <td className={`px-4 py-3 font-mono ${isVoided ? "line-through" : ""}`}>{i.number}</td>
                  {vis("client") && <td className="px-4 py-3 text-slate-700">{clientName}</td>}
                  {vis("workers") && <td className="px-4 py-3">
                    <span data-testid={`invoice-workers-${i.id}`}
                          title={(i._worker_last_names || []).join(", ")}
                          className="block max-w-[170px] truncate text-[11px] leading-tight text-slate-500">
                      {(i._worker_last_names || []).join(", ") || "—"}
                    </span>
                  </td>}
                  {vis("week_ending") && <td className="px-4 py-3 text-slate-600">{i.week_ending || "—"}</td>}
                  {vis("po_number") && <td className="px-4 py-3 font-mono text-slate-600">{i.po_number || "—"}</td>}
                  {vis("due_date") && <td className="px-4 py-3 text-slate-600">{i.due_date}</td>}
                  {vis("sent_at") && <td className="px-4 py-3 text-slate-600" data-testid={`invoice-sent-at-${i.id}`}>
                    {i.sent_at ? String(i.sent_at).slice(0, 10) : "—"}
                  </td>}
                  {vis("paid_date") && <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`invoice-paid-date-${i.id}`}>
                    {paidDateOf(i)
                      ? <>{paidDateOf(i)}{i.status === "partially_paid" && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">partial</span>}</>
                      : "—"}
                  </td>}
                  {vis("total") && <td className="px-4 py-3 text-right font-mono">{money(i.total)}</td>}
                  {vis("profit") && <td className="px-4 py-3 text-right font-mono">
                    {isVoided ? <span className="text-slate-400">—</span>
                      : i.pay_total != null
                      ? <span className="text-emerald-700 font-semibold">{money(i.total - i.pay_total)}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>}
                  {vis("status") && <td className="px-4 py-3 whitespace-nowrap">
                    {i._email_failed ? (
                      <span data-testid={`email-failed-${i.id}`}
                        title={`Invoice email ${i._last_email_status || "failed"}${i._last_email_at ? ` — ${i._last_email_at.slice(0, 16).replace("T", " ")} UTC` : ""}. Click Re-send to retry.`}>
                        <StatusPill status="failed" />
                      </span>
                    ) : (
                      <StatusPill status={i.status} />
                    )}
                  </td>}
                  {vis("last_reminder") && <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs" data-testid={`invoice-last-reminder-${i.id}`}>
                    {lastReminder(i) ? (
                      <span title={`${(i.reminders || []).length} reminder${(i.reminders || []).length === 1 ? "" : "s"} sent · last by ${(i.reminders || [])[(i.reminders || []).length - 1]?.by || "—"}`}>
                        {lastReminder(i).slice(0, 10)} <span className="text-slate-400">×{(i.reminders || []).length}</span>
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link data-testid={`invoice-open-${i.id}`} to={`/invoices/${i.id}`} className="text-green-600 font-semibold">Open</Link>
                    <a data-testid={`invoice-pdf-${i.id}`} href={`${API_BASE}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="ml-3 text-slate-600 font-semibold">PDF</a>
                    {isAdmin && !["draft", "voided"].includes(i.status) && (
                      <button onClick={() => resendInvoice(i)} disabled={resendingId === i.id}
                        data-testid={`resend-invoice-${i.id}`}
                        title="Re-send the invoice email to the client"
                        className={`ml-3 disabled:opacity-60 ${i._email_failed ? "font-bold text-red-600 hover:text-red-800" : "font-semibold text-slate-600 hover:text-slate-900"}`}>
                        {resendingId === i.id ? "Re-sending…" : "Re-send"}
                      </button>
                    )}
                    {isAdmin && ["sent", "partially_paid", "overdue"].includes(i.status) && (i.balance_due || 0) > 0 && (
                      <button onClick={() => openMarkPaid(i)}
                        data-testid={`invoice-mark-paid-${i.id}`}
                        className="ml-3 text-emerald-600 hover:text-emerald-800 font-semibold">Mark paid</button>
                    )}
                    {canRemind && (
                      <button onClick={() => remind(i.id)} disabled={remindingId === i.id}
                        data-testid={`invoice-remind-${i.id}`}
                        className={`ml-3 font-semibold disabled:opacity-60 ${i.status === "overdue" ? "text-rose-600 hover:text-rose-800" : "text-green-600 hover:text-green-800"}`}
                      >{remindingId === i.id ? "Sending…" : "Send reminder"}</button>
                    )}
                    {isAdmin && !["voided", "paid"].includes(i.status) && (
                      <button onClick={() => setVoidFor(i)}
                        data-testid={`invoice-void-${i.id}`}
                        title="Void invoice — keep it on record, zero the balance"
                        aria-label={`Void invoice ${i.number}`}
                        className="ml-3 text-slate-500 hover:text-slate-900 font-semibold align-middle inline-flex items-center gap-1">
                        <Prohibit size={14} weight="bold" /> Void
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => deleteInvoice(i)}
                        data-testid={`invoice-delete-${i.id}`}
                        title="Delete invoice permanently (not kept in records)"
                        aria-label={`Delete invoice ${i.number}`}
                        className="ml-3 text-slate-400 hover:text-rose-600 align-middle inline-flex items-center">
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={createBlank} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="new-invoice-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New (blank) invoice</h2>
              <button type="button" onClick={() => setCreating(false)}><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Creates an empty draft you can add line items to. For pre-existing outstanding invoices, use <b>Add historic invoice</b> instead.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Client</label>
                <ClientCombobox
                  value={blankForm.client_id}
                  onChange={(v) => setBlankForm({ ...blankForm, client_id: v, project_id: "" })}
                  clients={clients}
                  testId="new-invoice-client"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project</label>
                <select required disabled={!blankForm.client_id} value={blankForm.project_id}
                  onChange={(e) => setBlankForm({ ...blankForm, project_id: e.target.value })}
                  data-testid="new-invoice-project" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-100">
                  <option value="">{blankForm.client_id ? "Select…" : "Pick a client first"}</option>
                  {blankProjects.map((p) => <option key={p.id} value={p.id}>{p.name} — PO {p.po_number}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Terms</label>
                  <select value={blankForm.payment_terms} onChange={(e) => setBlankForm({ ...blankForm, payment_terms: e.target.value })}
                    className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md">
                    <option>Net 15</option><option>Net 30</option><option>Net 45</option><option>Net 60</option><option>Due on Receipt</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Due date (optional)</label>
                  <input type="date" value={blankForm.due_date} onChange={(e) => setBlankForm({ ...blankForm, due_date: e.target.value })}
                    className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setCreating(false)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="create-blank-invoice-btn" className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md">Create draft</button>
            </div>
          </form>
        </div>
      )}

      {historic && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <form onSubmit={createHistoric} className="bg-white rounded-md w-full max-w-2xl p-6 border border-slate-200 my-8" data-testid="historic-invoice-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClockCounterClockwise size={22} weight="duotone" className="text-slate-700" />
                <h2 className="text-xl font-bold">Add historic invoice</h2>
              </div>
              <button type="button" onClick={() => setHistoric(false)}><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Record a pre-SOS invoice for reporting and AR history. Mark it <b>Paid</b> for closed billing (does not affect Outstanding) or <b>Outstanding</b> to add it to receivables. Only client + amount are required.</p>

            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Status</div>
              <div className="flex gap-2">
                <label className={`flex-1 flex items-center justify-center gap-2 h-11 px-3 rounded-md border-2 cursor-pointer font-semibold text-sm ${!histForm.is_paid ? "border-amber-500 bg-amber-50 text-amber-900" : "border-slate-300 bg-white text-slate-600"}`}>
                  <input type="radio" name="hist-status" checked={!histForm.is_paid}
                    onChange={() => setHistForm({ ...histForm, is_paid: false, paid_date: "" })}
                    data-testid="hist-status-outstanding" className="sr-only" />
                  Outstanding (goes into AR)
                </label>
                <label className={`flex-1 flex items-center justify-center gap-2 h-11 px-3 rounded-md border-2 cursor-pointer font-semibold text-sm ${histForm.is_paid ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-white text-slate-600"}`}>
                  <input type="radio" name="hist-status" checked={histForm.is_paid}
                    onChange={() => setHistForm({ ...histForm, is_paid: true })}
                    data-testid="hist-status-paid" className="sr-only" />
                  Paid (reporting only)
                </label>
              </div>
              {histForm.is_paid && (
                <div className="mt-3">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Date paid</label>
                  <input type="date" value={histForm.paid_date}
                    onChange={(e) => setHistForm({ ...histForm, paid_date: e.target.value })}
                    data-testid="hist-paid-date" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
                  <div className="mt-1 text-xs text-slate-500">Optional — defaults to the invoice date if left blank.</div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Client *</label>
                <ClientCombobox
                  value={histForm.client_id}
                  onChange={(v) => setHistForm({ ...histForm, client_id: v, project_id: "" })}
                  clients={clients}
                  testId="hist-client"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Amount owed *</label>
                <input required type="number" step="0.01" min="0" value={histForm.amount}
                  onChange={(e) => setHistForm({ ...histForm, amount: e.target.value })}
                  data-testid="hist-amount" placeholder="0.00"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Week ending</label>
                <input type="date" value={histForm.week_ending}
                  onChange={(e) => setHistForm({ ...histForm, week_ending: e.target.value })}
                  data-testid="hist-week" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Invoice date</label>
                <input type="date" value={histForm.invoice_date}
                  onChange={(e) => setHistForm({ ...histForm, invoice_date: e.target.value })}
                  data-testid="hist-invoice-date" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Due date</label>
                <input type="date" value={histForm.due_date}
                  onChange={(e) => setHistForm({ ...histForm, due_date: e.target.value })}
                  data-testid="hist-due" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Terms</label>
                <select value={histForm.payment_terms} onChange={(e) => setHistForm({ ...histForm, payment_terms: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md">
                  <option>Net 15</option><option>Net 30</option><option>Net 45</option><option>Net 60</option><option>Due on Receipt</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Custom invoice # (optional)</label>
                <input value={histForm.number} onChange={(e) => setHistForm({ ...histForm, number: e.target.value })}
                  placeholder="Leave blank to auto-generate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" />
              </div>

              <div className="col-span-2 border-t border-slate-100 pt-3 mt-1">
                <div className="tinylabel mb-2">Optional details</div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">PO #</label>
                <input value={histForm.po_number} onChange={(e) => setHistForm({ ...histForm, po_number: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Sub / Project #</label>
                <input value={histForm.subcontractor_number} onChange={(e) => setHistForm({ ...histForm, subcontractor_number: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Existing project (optional)</label>
                <select disabled={!histForm.client_id} value={histForm.project_id}
                  onChange={(e) => setHistForm({ ...histForm, project_id: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-100 bg-white">
                  <option value="">{histForm.client_id ? "— None (free-text below) —" : "Pick a client first"}</option>
                  {histProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project name (free-text)</label>
                <input value={histForm.project_name} onChange={(e) => setHistForm({ ...histForm, project_name: e.target.value })}
                  disabled={!!histForm.project_id}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-100" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Location</label>
                <input value={histForm.project_location} onChange={(e) => setHistForm({ ...histForm, project_location: e.target.value })}
                  disabled={!!histForm.project_id}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-100" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Line description (optional)</label>
                <input value={histForm.description} onChange={(e) => setHistForm({ ...histForm, description: e.target.value })}
                  placeholder="Auto: 'Services — week ending YYYY-MM-DD'"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setHistoric(false)}
                className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="save-historic-btn"
                className="h-11 px-4 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-md">
                Record historic invoice
              </button>
            </div>
          </form>
        </div>
      )}
      {markPaidFor && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={doMarkPaid} className="bg-white rounded-md w-full max-w-md p-6 border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="mark-paid-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Mark paid — {markPaidFor.number}</h2>
              <button type="button" onClick={() => setMarkPaidFor(null)}><X size={20} /></button>
            </div>
            <div className="text-sm text-slate-600 mb-4 bg-slate-50 border border-slate-200 rounded-md p-3">
              <div><b>Balance due:</b> <span className="font-mono font-bold text-slate-900">{money(markPaidFor.balance_due)}</span></div>
              <div className="text-xs text-slate-500 mt-1">Records a payment for the full balance and moves the invoice to <b>Paid</b>.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Date of payment *</label>
                <input required type="date" value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  data-testid="pay-date-input"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Method</label>
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}
                  data-testid="pay-method-input"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                  <option value="check">Check</option>
                  <option value="ach">ACH / bank</option>
                  <option value="wire">Wire</option>
                  <option value="card">Credit card</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setMarkPaidFor(null)}
                className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="confirm-mark-paid-btn" disabled={payBusy}
                className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md disabled:opacity-60">
                {payBusy ? "Saving…" : "Mark paid in full"}
              </button>
            </div>
          </form>
        </div>
      )}
      {viewI && (
        <QuickView title={viewI.number}
                   subtitle={clients.find((c) => c.id === viewI.client_id)?.name || "—"}
                   status={viewI._email_failed ? "failed" : viewI.status} onClose={() => setViewI(null)} testId="invoice-quick-view">
          <QVRow label="Project" value={viewI.project_name} />
          <QVRow label="Workers" value={(viewI._worker_last_names || []).join(", ")} />
          <QVRow label="Location" value={viewI.project_location} />
          <QVRow label="Week ending" value={viewI.week_ending} mono />
          <QVRow label="PO #" value={viewI.po_number} mono />
          <QVRow label="Terms" value={viewI.payment_terms} />
          <QVRow label="Due date" value={viewI.due_date} mono />
          <QVRow label="Total" value={money(viewI.total)} mono />
          <QVRow label="Paid" value={money((viewI.total || 0) - (viewI.balance_due || 0))} mono />
          <QVRow label="Balance due" value={money(viewI.balance_due)} mono />
          <QVRow label="Pay total (cost)" value={viewI.pay_total != null ? money(viewI.pay_total) : null} mono />
          <QVRow label="Profit" value={viewI.pay_total != null ? money((viewI.total || 0) - viewI.pay_total) : null} mono />
          <QVRow label="Line items" value={(viewI.lines || []).length} mono />
          <QVRow label="Payments recorded" value={(viewI.payments || []).length} mono />
          {viewI._last_email_status && (
            <QVRow label="Last email" value={viewI._email_failed ? `FAILED (${viewI._last_email_status})` : viewI._last_email_status} />
          )}
          {isAdmin && !["draft", "voided"].includes(viewI.status) && (
            <button type="button" data-testid="qv-resend-invoice-btn"
              onClick={() => { const inv = viewI; setViewI(null); resendInvoice(inv); }}
              className={`mt-3 w-full h-10 rounded-md text-sm font-bold ${viewI._email_failed ? "bg-red-600 hover:bg-red-700 text-white" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
              Re-send invoice email
            </button>
          )}
          {viewI.status === "voided" && (
            <div className="mt-3 text-xs text-slate-600 bg-slate-100 border border-slate-300 rounded-md p-2" data-testid="qv-void-note">
              <b>Voided</b>{viewI.voided_at ? ` ${new Date(viewI.voided_at).toLocaleDateString()}` : ""}{viewI.void_reason ? ` — ${viewI.void_reason}` : ""}
            </div>
          )}
          {isAdmin && !["voided", "paid"].includes(viewI.status) && (
            <button type="button" data-testid="qv-void-invoice-btn"
              onClick={() => { const inv = viewI; setViewI(null); setVoidFor(inv); }}
              className="mt-2 w-full h-10 rounded-md text-sm font-bold border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-1">
              <Prohibit size={14} weight="bold" /> Void invoice (keep on record)
            </button>
          )}
        </QuickView>
      )}
      {showStatement && (
        <StatementModal clients={clients} projects={projects} onClose={() => setShowStatement(false)} />
      )}
      {voidFor && (
        <VoidInvoiceModal invoice={voidFor} onClose={() => setVoidFor(null)}
          onVoided={() => { setSelected((prev) => { const n = new Set(prev); n.delete(voidFor.id); return n; }); setVoidFor(null); load(); }} />
      )}
    </div>
  );
}
